/**
 * @file ShopEditDialog.tsx
 * @description «Изменить сведения» магазина (phase19.28 этап А; PL4, PL5):
 * название, часовой пояс, почта магазина. Slug не меняется (В6) — его в
 * окне нет. После сохранения карточка перечитывается.
 */

import { useState } from "react";

import { platformShops, type ShopCard } from "../../../lib/api-platform-shops";
import type { ShopTimeZoneOption } from "../../../lib/api-shop-settings";
import { isEmail } from "../../../lib/platform/shop-create-form";
import { apiErrorText, shopCreateError } from "../../../lib/platform/shop-errors";
import { ShopTimeZoneField } from "../../../pages/shop/settings/ShopTimeZoneField";
import { Alert } from "../../ui/alert";
import { Button } from "../../ui/button";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "../../ui/dialog";
import { useNotify } from "../../ui/NotifyProvider";
import { ShopTextField } from "./ShopTextField";

interface Props {
  shop: ShopCard;
  timeZones: ShopTimeZoneOption[];
  onClose: () => void;
  onSaved: () => void;
}

type Errors = Partial<Record<"displayName" | "timeZone" | "contactEmail" | "form", string>>;

export function ShopEditDialog({ shop, timeZones, onClose, onSaved }: Props) {
  const notify = useNotify();
  const [displayName, setDisplayName] = useState(shop.displayName);
  const [timeZone, setTimeZone] = useState(shop.timeZone);
  const [contactEmail, setContactEmail] = useState(shop.contactEmail ?? "");
  const [errors, setErrors] = useState<Errors>({});
  const [busy, setBusy] = useState(false);

  async function save() {
    const next: Errors = {};
    if (!displayName.trim()) next.displayName = "Укажите название магазина";
    if (contactEmail.trim() && !isEmail(contactEmail)) next.contactEmail = "Почта записана неверно";
    setErrors(next);
    if (Object.keys(next).length > 0) return;
    setBusy(true);
    try {
      await platformShops.update(shop.id, {
        displayName: displayName.trim(),
        timeZone,
        contactEmail: contactEmail.trim(),
      });
      notify.success("Сведения магазина сохранены");
      onSaved();
    } catch (err) {
      const { field, message } = shopCreateError(err);
      if (field === "timeZone" || field === "displayName" || field === "contactEmail") setErrors({ [field]: message });
      else setErrors({ form: apiErrorText(err, "Не удалось сохранить сведения. Повторите попытку") });
    } finally {
      setBusy(false);
    }
  }

  return (
    <Dialog open onOpenChange={(open) => !open && !busy && onClose()}>
      <DialogContent fullScreen className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>Сведения магазина</DialogTitle>
        </DialogHeader>
        <div className="flex flex-col gap-4">
          <ShopTextField
            id="shop-edit-name"
            label="Название"
            required
            value={displayName}
            onChange={setDisplayName}
            error={errors.displayName}
            maxLength={120}
          />
          <p className="type-caption text-on-surface-variant">Адрес (slug) «{shop.slug}» не меняется: от него зависят поддомен и все ссылки.</p>
          <ShopTimeZoneField value={timeZone} options={timeZones} onChange={setTimeZone} error={errors.timeZone} required />
          <ShopTextField
            id="shop-edit-email"
            label="Почта магазина"
            type="email"
            value={contactEmail}
            onChange={setContactEmail}
            error={errors.contactEmail}
          />
          {errors.form ? <Alert severity="error">{errors.form}</Alert> : null}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={busy}>
            Отмена
          </Button>
          <Button onClick={() => void save()} loading={busy}>
            Сохранить
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
